import { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, Volume2, Square, BookOpen } from 'lucide-react';
import { speak, stopSpeaking, shuffleQuiz } from './api';

const STORIES = [
  {
    id: 'fox-grapes', title: 'The Fox and the Grapes', level: 'A2',
    text: `One hot summer day, a hungry fox was walking through a forest. He saw a bunch of ripe grapes hanging from a high branch. "Those grapes look delicious," he thought. The fox jumped up to reach them, but he missed. He tried again and again, jumping as high as he could, but the grapes were too high. Finally, tired and annoyed, he walked away. "I didn't want those grapes anyway," he said. "They are probably sour." It is easy to hate what you cannot have.`,
    questions: [
      { q: 'What did the fox want to eat?', options: ['Grapes', 'Apples', 'Berries'], answer: 0 },
      { q: "Why couldn't the fox reach the grapes?", options: ['They were too high', 'A dog chased him', 'It was raining'], answer: 0 },
      { q: 'What did the fox say at the end?', options: ['The grapes are probably sour', 'He would buy grapes', 'He was very happy'], answer: 0 },
    ],
  },
  {
    id: 'city-mouse', title: 'The City Mouse and the Country Mouse', level: 'B1',
    text: `A country mouse invited his friend, a city mouse, to visit his home in the fields. He offered simple food: corn, roots and fresh water. The city mouse was not impressed. "Come to the city with me," he said, "and you will eat like a king." So they travelled to a grand house full of delicious food. But every time they started to eat, a cat appeared and they had to run and hide. The country mouse was frightened. "I prefer my simple food in peace," he said, "to a feast full of fear." Then he went home, happy and safe.`,
    questions: [
      { q: 'What food did the country mouse offer?', options: ['Cake', 'Corn, roots and water', 'Cheese'], answer: 1 },
      { q: 'Why did the mice keep hiding in the city?', options: ['A cat appeared', 'It was dark', 'People shouted'], answer: 0 },
      { q: 'What did the country mouse prefer?', options: ['A feast full of fear', 'Simple food in peace', 'City life'], answer: 1 },
    ],
  },
  {
    id: 'first-day', title: 'My First Day at a New School', level: 'B1',
    text: `Maria moved to a new town during the summer. In September, she started at a new school where she knew no one. On her first morning, she felt nervous and a little scared. She walked into the classroom quietly and sat near the window. During the break, a girl named Aisha came to talk to her. They discovered they both loved reading and drawing. By the end of the day, Maria had made her first friend. She realised that new beginnings can be difficult, but they can also bring wonderful surprises.`,
    questions: [
      { q: 'Why did Maria feel nervous?', options: ['It was a new school', 'She was late', 'She lost her bag'], answer: 0 },
      { q: 'Who came to talk to Maria?', options: ['A teacher', 'A girl named Aisha', 'Her brother'], answer: 1 },
      { q: 'What did Maria learn?', options: ['New beginnings can bring surprises', 'School is boring', 'To stay home'], answer: 0 },
    ],
  },
  {
    id: 'goose-golden-eggs', title: 'The Goose and the Golden Eggs', level: 'A2',
    text: `A farmer had a goose that laid one golden egg every day. The farmer became rich, but he was not happy. He wanted all the gold at once. "There must be a pile of gold inside this goose," he thought. So he killed the goose to take the gold. But inside, there was nothing at all. Now he had no eggs and no goose. He had lost everything because of his greed.`,
    questions: [
      { q: 'What did the goose lay every day?', options: ['A golden egg', 'A silver coin', 'A diamond'], answer: 0 },
      { q: 'Why did the farmer lose everything?', options: ['Because of his greed', 'Because of the rain', 'Because he was tired'], answer: 0 },
    ],
  },
  {
    id: 'crow-pitcher', title: 'The Crow and the Pitcher', level: 'A2',
    text: `On a very hot day, a thirsty crow found a pitcher with a little water at the bottom. He put his beak in, but he could not reach the water. The crow thought for a moment. Then he picked up small stones and dropped them, one by one, into the pitcher. Slowly, the water rose higher and higher. At last, the clever crow could drink. Little by little, with patience, we can solve big problems.`,
    questions: [
      { q: 'What did the crow drop into the pitcher?', options: ['Small stones', 'Leaves', 'Sand'], answer: 0 },
      { q: 'What is the lesson?', options: ['Patience solves problems', 'Crows are lazy', 'Water is heavy'], answer: 0 },
    ],
  },
  {
    id: 'dog-reflection', title: 'The Dog and His Reflection', level: 'A2',
    text: `A dog found a big bone and carried it home across a bridge. Looking down into the river, he saw his own reflection in the water. He thought it was another dog with a bigger bone. Greedy, he opened his mouth to grab the other bone — and his own bone fell into the water and was lost forever. If you want too much, you may lose what you already have.`,
    questions: [
      { q: 'What did the dog see in the water?', options: ['His own reflection', 'A fish', 'Another bridge'], answer: 0 },
      { q: 'What happened to his bone?', options: ['It fell into the water', 'He ate it', 'He hid it'], answer: 0 },
    ],
  },
  {
    id: 'ant-and-dove', title: 'The Ant and the Dove', level: 'B1',
    text: `An ant fell into a river and was about to drown. A kind dove saw this and dropped a leaf onto the water. The ant climbed onto the leaf and floated safely to the shore. Later, a hunter came and pointed his gun at the dove. The grateful ant bit the hunter's foot. He cried out, and the dove flew away safely. One good turn deserves another.`,
    questions: [
      { q: 'How did the dove save the ant?', options: ['It dropped a leaf', 'It called for help', 'It flew away'], answer: 0 },
      { q: 'How did the ant help the dove?', options: ['It bit the hunter', 'It sang a song', 'It hid the gun'], answer: 0 },
    ],
  },
  {
    id: 'milkmaid-pail', title: 'The Milkmaid and Her Pail', level: 'B1',
    text: `A young milkmaid was walking to market with a pail of milk on her head. As she walked, she dreamed. "I will sell this milk and buy eggs. The eggs will give me chickens. I will sell the chickens and buy a beautiful dress." Thinking of the dress, she tossed her head proudly — and the pail fell, spilling all the milk. Her dreams were gone. Do not count your chickens before they hatch.`,
    questions: [
      { q: 'What was the milkmaid carrying?', options: ['A pail of milk', 'A basket of eggs', 'A bag of gold'], answer: 0 },
      { q: 'Why did she lose the milk?', options: ['She tossed her head and it spilled', 'A dog pushed her', 'It started to rain'], answer: 0 },
    ],
  },
  {
    id: 'honest-woodcutter', title: 'The Honest Woodcutter', level: 'B1',
    text: `A poor woodcutter dropped his axe into a deep river. A kind spirit appeared and brought up a golden axe. "Is this yours?" she asked. "No," said the man honestly. Then she brought a silver axe. Again he said no. Finally she brought his old iron axe. "Yes, that is mine!" he said happily. Pleased with his honesty, the spirit gave him all three axes. Honesty is always rewarded.`,
    questions: [
      { q: 'What did the woodcutter drop in the river?', options: ['His axe', 'His coat', 'His bread'], answer: 0 },
      { q: 'Why did the spirit reward him?', options: ['For his honesty', 'For his strength', 'For his speed'], answer: 0 },
    ],
  },
  {
    id: 'boy-and-nuts', title: 'The Boy and the Nuts', level: 'A2',
    text: `A little boy put his hand into a jar full of nuts. He grabbed as many as he possibly could. But when he tried to pull his hand out, it was stuck — the opening was too small for such a big fistful. He began to cry. His mother said gently, "Take only half, and you will easily pull your hand out." The boy did, and he was happy. Do not try to take too much at once.`,
    questions: [
      { q: 'Why was the boy\'s hand stuck?', options: ['He grabbed too many nuts', 'The jar was broken', 'His hand was wet'], answer: 0 },
      { q: 'What did his mother advise?', options: ['Take only half', 'Break the jar', 'Buy more nuts'], answer: 0 },
    ],
  },
  {
    id: 'lost-dog', title: 'The Lost Dog', level: 'B1',
    text: `One rainy evening, Thomas found a small dog shivering near his door. The dog had no collar and looked hungry. Thomas gave it food and a warm blanket. The next day, he put up posters around the neighbourhood. A week later, a little girl knocked on his door. She cried with joy when she saw the dog — it was her lost puppy, Max. Thomas felt happy that a simple act of kindness had brought them back together.`,
    questions: [
      { q: 'Where did Thomas find the dog?', options: ['Near his door', 'In the park', 'At school'], answer: 0 },
      { q: 'Who came to claim the dog?', options: ['A little girl', 'A police officer', 'His neighbour'], answer: 0 },
    ],
  },
  {
    id: 'north-star', title: 'Following the North Star', level: 'B2',
    text: `For thousands of years, travellers and sailors used the stars to find their way. The most important of these was the North Star, which always points north. Unlike the other stars, it does not move across the sky. Lost in a dark forest or sailing on a wide ocean, people only had to find this one bright star to know which direction to go. Even today, when we feel lost, it helps to find our own "north star" — a clear goal to guide us forward.`,
    questions: [
      { q: 'Why was the North Star so useful?', options: ['It always points north', 'It is the brightest planet', 'It changes colour'], answer: 0 },
      { q: 'What does a "north star" mean for us today?', options: ['A clear goal to guide us', 'A type of map', 'A new phone'], answer: 0 },
    ],
  },
  {
    id: 'market-day', title: 'A Day at the Market', level: 'A2',
    text: `Every Saturday morning, Grace goes to the local market with her grandmother. The market is full of colour and noise. There are oranges, tomatoes, fresh fish and bright flowers everywhere. Grace loves to taste the sweet fruit and listen to the sellers calling out their prices. Her grandmother teaches her how to choose the best vegetables and how to greet people politely. For Grace, the market is not only a place to buy food — it is a place to learn and to share time with family.`,
    questions: [
      { q: 'When does Grace go to the market?', options: ['Every Saturday morning', 'Every night', 'Once a year'], answer: 0 },
      { q: 'What does her grandmother teach her?', options: ['How to choose the best vegetables', 'How to drive', 'How to swim'], answer: 0 },
    ],
  },
  {
    id: 'two-travellers', title: 'The Two Travellers and the Bear', level: 'B1',
    text: `Two friends were walking through a forest when a big bear suddenly appeared. One friend quickly climbed a tree and hid, thinking only of himself. The other had no time, so he lay on the ground and pretended to be dead. The bear sniffed his ear and then walked away, because bears do not touch the dead. When the first man came down, he asked, "What did the bear whisper to you?" His friend replied, "He told me never to trust a friend who leaves you in danger."`,
    questions: [
      { q: 'What did the second traveller do?', options: ['Pretended to be dead', 'Ran away fast', 'Fought the bear'], answer: 0 },
      { q: 'What is the lesson?', options: ['A true friend does not leave you in danger', 'Bears are friendly', 'Always climb trees'], answer: 0 },
    ],
  },
  {
    id: 'the-dream', title: 'Chasing a Dream', level: 'B2',
    text: `When Daniel was young, everyone told him his dream was impossible. He wanted to become an engineer, but his family was poor and schools were far away. Instead of giving up, Daniel studied at night by the light of a small lamp. He read every book he could find and never stopped asking questions. Years later, after much hard work, he built his first machine. Daniel often says that talent is useful, but patience and effort are what truly change a life.`,
    questions: [
      { q: 'How did Daniel study?', options: ['At night by a small lamp', 'Only at school', 'He did not study'], answer: 0 },
      { q: 'What truly changed his life, according to him?', options: ['Patience and effort', 'Luck', 'Money'], answer: 0 },
    ],
  },
];

export default function Audiobooks({ onBack }) {
  const [active, setActive] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [answers, setAnswers] = useState({});
  const [score, setScore] = useState(null);
  const [quiz, setQuiz] = useState([]);

  const open = (s) => { setActive(s); setQuiz(shuffleQuiz(s.questions)); setAnswers({}); setScore(null); stopSpeaking(); setPlaying(false); };
  const listen = () => {
    if (playing) { stopSpeaking(); setPlaying(false); return; }
    setPlaying(true);
    speak(active.text, 'en-US');
    setTimeout(() => setPlaying(false), Math.min(active.text.length * 70, 90000));
  };
  const grade = () => {
    let s = 0;
    quiz.forEach((q, qi) => { if (answers[qi] === q.answer) s += 1; });
    setScore(s);
  };

  if (active) {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-3xl mx-auto px-4 py-8" data-testid="audiobook-reader">
        <button onClick={() => { stopSpeaking(); setActive(null); }} className="text-white/60 hover:text-white text-sm mb-6 flex items-center gap-1"><ChevronLeft size={16} />Back to library</button>
        <div className="flex items-center justify-between gap-3 mb-4">
          <div>
            <h1 className="text-2xl font-black">{active.title}</h1>
            <span className="text-[11px] uppercase tracking-wider text-emerald-300">CEFR {active.level}</span>
          </div>
          <button onClick={listen} data-testid="audiobook-listen" className="flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold px-4 py-2 rounded-full">
            {playing ? <><Square size={16} />Stop</> : <><Volume2 size={16} />Listen</>}
          </button>
        </div>
        <div className="bg-white/5 border border-white/10 rounded-2xl p-5 text-white/85 leading-relaxed mb-6" data-testid="audiobook-text">{active.text}</div>

        <div className="bg-emerald-500/5 border border-emerald-500/25 rounded-2xl p-5">
          <div className="font-bold mb-1 flex items-center gap-2"><BookOpen size={16} className="text-emerald-300" />Comprehension questions</div>
          <p className="text-sm text-white/60 mb-4">Listen, read, then choose the correct answer.</p>
          <div className="space-y-5">
            {quiz.map((q, qi) => (
              <div key={qi} data-testid={`audiobook-q-${qi}`}>
                <div className="font-semibold mb-2 text-sm">{qi + 1}. {q.q}</div>
                <div className="space-y-2">
                  {q.options.map((opt, oi) => {
                    const chosen = answers[qi] === oi;
                    const isCorrect = q.answer === oi;
                    let cls = 'bg-white/5 hover:bg-white/10 border-white/10';
                    if (score !== null) {
                      if (isCorrect) cls = 'bg-emerald-500/20 border-emerald-400/50';
                      else if (chosen) cls = 'bg-red-500/20 border-red-400/50';
                    } else if (chosen) cls = 'bg-emerald-500/20 border-emerald-400/50';
                    return (
                      <button key={oi} disabled={score !== null} onClick={() => setAnswers(a => ({ ...a, [qi]: oi }))}
                        data-testid={`audiobook-q-${qi}-opt-${oi}`}
                        className={`w-full text-left px-3 py-2 rounded-lg border transition text-sm ${cls}`}>{opt}</button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          {score === null ? (
            <button onClick={grade} disabled={quiz.some((_, qi) => answers[qi] === undefined)}
              className="mt-4 w-full bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 text-slate-950 font-bold py-2.5 rounded-xl" data-testid="audiobook-submit">
              Check my answers
            </button>
          ) : (
            <div className="mt-4 text-center bg-emerald-500/10 border border-emerald-400/30 rounded-xl p-4" data-testid="audiobook-score">
              <div className="text-3xl font-black text-emerald-300">{score}/{quiz.length}</div>
              <div className="text-white/70 text-sm mt-1">{score === quiz.length ? 'Perfect! 🎉' : 'Check the answers in green 👍'}</div>
              <button onClick={() => { setScore(null); setAnswers({}); }} className="mt-3 text-xs text-emerald-300 hover:underline" data-testid="audiobook-retry">Try again</button>
            </div>
          )}
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-3xl mx-auto px-4 py-8" data-testid="audiobooks-page">
      <button onClick={onBack} className="text-white/60 hover:text-white text-sm mb-6 flex items-center gap-1"><ChevronLeft size={16} />Back</button>
      <h1 className="text-2xl font-black mb-1">Audiobooks & Stories</h1>
      <p className="text-white/60 text-sm mb-6">Listen to a story, read along, then answer a few questions — the best way to train your listening and comprehension.</p>
      <div className="space-y-3">
        {STORIES.map(s => (
          <button key={s.id} onClick={() => open(s)} data-testid={`audiobook-card-${s.id}`}
            className="w-full flex items-center gap-3 bg-white/5 hover:bg-emerald-500/10 border border-white/10 hover:border-emerald-400/30 rounded-2xl p-4 text-left transition">
            <span className="text-3xl">📖</span>
            <div className="flex-1">
              <div className="font-bold">{s.title}</div>
              <div className="text-[11px] uppercase tracking-wider text-emerald-300/70">CEFR {s.level} · Listen & answer</div>
            </div>
            <Volume2 size={18} className="text-emerald-300" />
          </button>
        ))}
      </div>
    </motion.div>
  );
}
